import { cloneDeep, isEmpty, sortBy, get } from 'lodash'
import { ProjectTabs } from '@/modules/projects/consts'
import { TenantProjectTabs } from '@/modules/tenantProjects/consts'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS, ALL_FLOORS, CreateWorkPermitTabs } from '@/modules/workPermits/consts'
import store from '@/store'
import { aclHelper, tmsPermsTypes, tenantPermsTypes, contractorPermsTypes } from '@/utils/aclHelper'
import i18n from '@/utils/i18n'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { standaloneWpRouteNames } from '@/utils/consts'
import { LIST_NAV_KIND } from '@/constants/listNavigation'
import { getRegionalWorkPermitLabel } from '@/modules/workPermits/regionalWorkPermitLabels'

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Read history row status from top-level or Sequelize-style dataValues.
 * @param {Object} row
 * @returns {*}
 */
function historyRowStatus (row) {
  if (row == null || typeof row !== 'object') return null
  if (row.status != null && row.status !== '') return row.status
  if (row.dataValues && row.dataValues.status != null && row.dataValues.status !== '') {
    return row.dataValues.status
  }
  return null
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Compare two history statuses with case-insensitive string fallback.
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function historyStatusEquals (a, b) {
  if (a == null || b == null) return false
  if (a === b) return true
  return String(a).toLowerCase() === String(b).toLowerCase()
}

function historyRowCreatedAt (row) {
  if (row == null || typeof row !== 'object') return null
  if (row.createdAt != null && row.createdAt !== '') return row.createdAt
  if (row.dataValues && row.dataValues.createdAt != null && row.dataValues.createdAt !== '') {
    return row.dataValues.createdAt
  }
  return null
}

function historyRowDetail (row) {
  if (row == null || typeof row !== 'object') return null
  if (row.detail != null) return row.detail
  if (row.dataValues && row.dataValues.detail != null) return row.dataValues.detail
  return null
}

export function groupWorkPermits (list = []) {
  let permits = {}
  list.forEach(permit => {
    switch (permit.type) {
      case WORK_PERMIT_TYPES.HOISTING:
        if (!permits[permit.parentId]) {
          initPermitGroup(permits, permit.parentId)
        }
        permits[permit.parentId].hoistings.push(permit)
        break
      case WORK_PERMIT_TYPES.PROJECT:
      case WORK_PERMIT_TYPES.WORK:
        if (!permits[permit.id]) {
          initPermitGroup(permits, permit.id)
        }
        permits[permit.id].self = permit
        break
      default:
        // Do nothing
    }
  })
  return sortBy(Object.values(permits), 'self.startDate')
}

function initPermitGroup (group, id) {
  if (!id) return
  group[id] = {
    self: {},
    hoistings: [],
  }
}

export function navigate (permit = {}, hoistingId, { fromViewAll = false, underBatch = false, standaloneTabIndex = '' } = {}) {
  return permit.standalone
    ? navToStandaloneWP({ hoistingId, permit, underBatch, fromViewAll, standaloneTabIndex })
    : navToNonStandaloneWP({ hoistingId, permit, underBatch, fromViewAll })
}

export function buildWorkPermitListNavigationEntry (permit = {}, hoistingId, { underBatch = false } = {}) {
  const id = hoistingId || permit.id
  if (!id) return null
  const isHoisting = permit.type === WORK_PERMIT_TYPES.HOISTING
  const query = {}
  if (hoistingId) query.hoistingId = hoistingId
  if (underBatch) query.underBatch = true

  if (permit.standalone) {
    const isEngineer = store.getters['user/isEngineer']
    const name = isEngineer
      ? 'engineerStandaloneWp'
      : permit.isTenantProject ? 'tenantStandaloneWp' : 'vendorStandaloneWp'
    return {
      id,
      kind: LIST_NAV_KIND.WORK_PERMIT,
      routeName: name,
      params: { id: permit.parentId || permit.id, prev: 'workPermitDashboard' },
      query,
    }
  }

  const isTenant = store.getters['user/isTenant']
  const isContractor = store.getters['user/isContractor']
  const navInfo = getNonStandaloneNavInfo(permit, isHoisting, isTenant, isContractor, hoistingId)
  const entryQuery = { ...navInfo.query, ...query }
  if (entryQuery.showDetail) {
    entryQuery.wpId = isHoisting ? permit.parentId : permit.id
  }
  return {
    id,
    kind: LIST_NAV_KIND.WORK_PERMIT,
    routeName: navInfo.name,
    params: { ...navInfo.params, prev: 'workPermitDashboard' },
    query: entryQuery,
  }
}

function navToStandaloneWP ({hoistingId, permit, underBatch = false, fromViewAll = false, standaloneTabIndex}) {
  const isEngineer = store.getters['user/isEngineer']
  const name = isEngineer
    ? 'engineerStandaloneWp'
    : permit.isTenantProject ? 'tenantStandaloneWp' : 'vendorStandaloneWp'

  let query = {}
  if (hoistingId) {
    query.hoistingId = hoistingId
  }

  if (underBatch) {
    query.underBatch = true
  }

  let params = standaloneTabIndex
    ? { id: permit.parentId || permit.id, tabIndex: standaloneTabIndex }
    : { id: permit.parentId || permit.id }

  if (fromViewAll) {
    params.prev = 'allWorkPermits'
    navigation.toPage(name, params, query)
  } else {
    navigation.replacePage(name, params, query)
  }
}

function navToNonStandaloneWP ({ hoistingId, permit, underBatch = false, fromViewAll = false }) {
  const isTenant = store.getters['user/isTenant']
  const isContractor = store.getters['user/isContractor']
  const isHoisting = permit.type === WORK_PERMIT_TYPES.HOISTING
  const navInfo = getNonStandaloneNavInfo(permit, isHoisting, isTenant, isContractor, hoistingId)

  let name = navInfo.name
  let query = navInfo.query
  let params = navInfo.params

  if (hoistingId) {
    query.hoistingId = hoistingId
  }

  if (query.showDetail) {
    query.wpId = isHoisting ? permit.parentId : permit.id
  }

  if (underBatch) {
    query.underBatch = true
  }

  if (fromViewAll) {
    params.prev = 'allWorkPermits'
    navigation.toPage(name, params, query)
  } else {
    query.from = permit.isTenantProject ? 'tenantProjectDetail' : 'vendorProjectDetail'
    navigation.replacePage(name, params, query)
  }
}

function getNonStandaloneNavInfo (permit, isHoisting, isTenant, isContractor, hoistingId) {
  let name = ''
  let query = {}
  let params = {}
  if (permit.status === WORK_PERMIT_STATUS.DRAFT) {
    if (isHoisting) {
      name = permit.isTenantProject
        ? (isTenant ? 'tenantProjectDetail' : 'tenantProject')
        : (isContractor ? 'vendorProjectDetail' : 'projectDetail')

      params = { id: permit.projectId }
      query = {
        tabIndex: (permit.isTenantProject ? TenantProjectTabs.workPermit : ProjectTabs.workPermit),
        showDetail: true,
      }
    } else {
      name = isTenant ? 'tenantCreateWorkPermit' : 'vendorCreateWorkPermit'
      params = { id: permit.id }
      // open HoistingHotWork tab directly if there's hoistingId
      if (hoistingId) {
        query.tabIndex = CreateWorkPermitTabs.hoistingHotWork
      }
    }
  } else if (permit.isTenantProject) {
    name = isTenant ? 'tenantProjectDetail' : 'tenantProject'
    params = { id: permit.projectId }
    query = { tabIndex: TenantProjectTabs.workPermit, showDetail: true }
  } else {
    name = isContractor ? 'vendorProjectDetail' : 'projectDetail'
    params = { id: permit.projectId }
    query = { tabIndex: ProjectTabs.workPermit, showDetail: true }
  }
  return { name, params, query }
}

export function engineerHasAccess (item) {
  const buildings = store.getters['user/buildings']
  const isEngineer = store.getters['user/isEngineer']
  const project = item.project || (item.isTenantProject ? store.getters['tenantProject/currentTenantProject'] : store.getters['projects/currentProject'])
  // TODO @Michelle update for CMS project building
  const building = item.isTenantProject ? get(project, 'tenancy.building') : get(project, 'building.code')
  const hasAccess = aclHelper.getAccess(tmsPermsTypes.workpermit.read)
  return isEngineer && item.status !== WORK_PERMIT_STATUS.DRAFT &&
    building && buildings.includes(building) && hasAccess
}

export function allowCreate () {
  return aclHelper.hasTmsAccess(tenantPermsTypes.projectContractor.readUpdate) || aclHelper.hasTmsAccess(contractorPermsTypes.projectContractor.readUpdate)
}

export function deletePPNavigate (workPermit) {
  let name = ''
  const isTenant = store.getters['user/isTenant']
  const isContractor = store.getters['user/isContractor']

  if (workPermit.isTenantProject) {
    name = isTenant ? 'tenantProjectDetail' : 'tenantProject'
  } else {
    name = isContractor ? 'vendorProjectDetail' : 'projectDetail'
  }
  const params = {
    id: workPermit.projectId,
  }
  const query = {
    tabIndex: workPermit.isTenantProject ? TenantProjectTabs.workPermit : ProjectTabs.workPermit,
  }
  navigation.replacePage(name, params, query)
}

export function getAttrOfBuilding (item, attr = 'fullName') {
  let building = ''
  const buildingAttrPath = `tenancy.building.${attr}`
  if (item.isTenantProject) {
    if (item.standalone) {
      building = get(item, buildingAttrPath)
    } else {
      const project = item.project || store.getters['tenantProject/currentTenantProject']
      building = get(project, buildingAttrPath)
    }
  } else {
    const currentWorkPermit = store.getters['workPermit/currentWorkPermit']
    const selectedBuilding = item.building || currentWorkPermit.building
    building = get(selectedBuilding, attr)
  }
  return building
}

export function getBuilding (item, fullName = true) {
  let building = ''
  const buildingFullNamePath = 'tenancy.building.fullName'
  const buildingCodePath = 'tenancy.building.code'
  const isTenant = store.getters['user/isTenant']
  const isContractor = store.getters['user/isContractor']

  if (item.isTenantProject) {
    if (item.standalone) {
      building = get(item, fullName ? buildingFullNamePath : buildingCodePath)
    } else {
      const project = item.project || store.getters['tenantProject/currentTenantProject']
      building = get(project, isTenant && fullName ? buildingFullNamePath : buildingCodePath)
    }
  } else {
    const currentWorkPermit = store.getters['workPermit/currentWorkPermit']
    const selectedBuilding = item.building || currentWorkPermit.building
    building = isContractor ? get(selectedBuilding, fullName ? 'fullName' : 'code') : get(selectedBuilding, 'code')
  }
  return building
}

export function getLastHistoryByStatusWithoutResume (histories, status, startStatus = WORK_PERMIT_STATUS.WAPPR) {
  let latestHistory
  let getIndex
  if (histories) {
    const latestHistories = sortBy(histories, history => {
      return new Date(historyRowCreatedAt(history))
    })
    // To fix HKLDCMS-22384 get latest histories from WAPPR, so it can get new process from latest WAPPR.
    for (let i = 0; i < latestHistories.length; i++) {
      const row = latestHistories[i]
      const detail = historyRowDetail(row)
      if (historyStatusEquals(historyRowStatus(row), startStatus) && !(detail && detail.isResume)) {
        getIndex = i
      }
    }
    const from = getIndex != null ? getIndex : 0
    const latestRecords = histories.length ? latestHistories.slice(from, latestHistories.length).reverse() : []
    latestHistory = latestRecords.find((record) => {
      if (!historyStatusEquals(historyRowStatus(record), status)) return false
      const d = historyRowDetail(record)
      /* Terminal APPR/REJECT rows must not be dropped because of a stray `isResume` flag on `detail`. */
      if (status === WORK_PERMIT_STATUS.REJECTED || status === WORK_PERMIT_STATUS.APPR) {
        return !(d && d.isReassign)
      }
      return !(d && d.isResume) && !(d && d.isReassign)
    })
  }
  return latestHistory
}

export function formatFloors (workPermit, building, buildingFloorZoneMap = {}) {
  return !building || isEmpty(buildingFloorZoneMap) ? '--'
    : sortBy(cloneDeep(get(workPermit, 'floors', [])), item => {
      return buildingFloorZoneMap[building]['floorIndex'][item] - 0
    }).join(', ')
}

export function floorsConsistent (workPermit = {}, floors = []) {
  const wpFloors = workPermit.floors || []
  const inconsistent = wpFloors[0] === ALL_FLOORS ? false : floors.some(floor => !wpFloors.includes(floor))
  if (inconsistent) {
    notification.fireNotification(false, i18n.t('error.inconsistentData',
      [i18n.t('workPermit.floor').toLowerCase(), i18n.t('workPermit.title'), getRegionalWorkPermitLabel('hoisting')]))
  }
  return !inconsistent
}

export function getStandaloneDetailRouteName (isEngineer, isTenantProject) {
  return isEngineer
    ? standaloneWpRouteNames.engineerStandaloneWp
    : isTenantProject ? standaloneWpRouteNames.tenantStandaloneWp : standaloneWpRouteNames.vendorStandaloneWp
}
